import { Stream } from 'stream';
import {
    IAudiogramResponse,
    IDeviceScanOnFile,
    IOption,
    IOrderDetails,
    IPatientDevice,
    IProductDetail,
    IReceiverPart,
    IRelatedItem,
} from '../models/product-detail';
import { IProductSearchResponse } from '../models/product-list';
import { IResult } from '../models/result-models';
import { apiGet, apiPost, apiPostFileUpload } from './api-utils';

const baseUrl = process.env.REACT_APP_API_URL + '/v1/product';
const orderBaseUrl = process.env.REACT_APP_API_URL + '/v1/order';
export const getProducts = async (
    categoryId: string | null,
    categoryName: string | null,
    query: string | null,
    attributeValueIds: string | null,
    attributeDefinitions: string | null,
    isCustom: boolean,
    pageNumber: number
): Promise<IResult<IProductSearchResponse>> => {
    const result = await apiGet<IProductSearchResponse>(
        baseUrl +
            '?categoryId=' +
            categoryId +
            '&categoryName=' +
            categoryName +
            '&query=' +
            query +
            '&attributeValueIds=' +
            attributeValueIds +
            '&attributeDefinitions=' +
            attributeDefinitions +
            '&isCustom=' +
            isCustom +
            '&pageNumber=' +
            pageNumber
    );
    return result;
};

export const getProductDetails = async (
    guid: string,
    cId: string
): Promise<IResult<IProductDetail>> => {
    const result = await apiGet<IProductDetail>(
        `${baseUrl}/get-product-details/${guid}?c=${cId}`
    );
    return result;
};

export const addToCart = async (
    order: IOrderDetails
): Promise<IResult<boolean>> => {
    const result = await apiPost<IOrderDetails, boolean>(
        `${baseUrl}/add-to-cart`,
        order
    );
    return result;
};

export const addReceiversToCart = async (
    receivers: IReceiverPart[]
): Promise<IResult<boolean>> => {
    const result = await apiPost<IReceiverPart[], boolean>(
        `${baseUrl}/add-receivers-to-cart`,
        receivers
    );
    return result;
};

//#region Product Detail Page fields validation API calls

export const getDeviceScanOnFile = async (
    serialNumber: string
): Promise<IResult<IDeviceScanOnFile>> => {
    const result = await apiGet<IDeviceScanOnFile>(
        `${baseUrl}/get-device-scan-on-file/${serialNumber}`
    );
    return result;
};

export const getDeviceHistory = async (
    serialNumber: string
): Promise<IResult<IPatientDevice>> => {
    const result = await apiGet<IPatientDevice>(
        `${baseUrl}/get-device-history/${serialNumber}`
    );
    return result;
};

export const uploadDigitalImpressions = async (
    files: FormData
): Promise<IResult<string>> => {
    const result = await apiPostFileUpload<string>(
        `${baseUrl}/upload-digital-impressions`,
        files
    );
    return result;
};

export const getMatrixAndLossType = async (
    catalogId: number,
    designId: number,
    audio: string,
    isAP: boolean
): Promise<IResult<IAudiogramResponse>> => {
    // Task 111962 - added isAP to call and URL/endpoint
    const result = await apiGet<IAudiogramResponse>(
        `${baseUrl}/get-matrix-and-loss-type/catalogs/${catalogId}/design/${designId}/${audio}/${isAP}`
    );
    return result;
};

export const getVent = async (
    catalogId: number,
    designId: number,
    r500: number | undefined,
    r1k: number | undefined
): Promise<IResult<string>> => {
    const result = await apiGet<string>(
        `${baseUrl}/get-vent/catalogs/${catalogId}/design/${designId}?r500=${r500}&r1k=${r1k}`
    );
    return result;
};

export const getImage = async (
    catalogId: number,
    designId: number
): Promise<IResult<Stream>> => {
    const result = await apiGet<Stream>(
        `${baseUrl}/get-image/catalogs/${catalogId}/design/${designId}`
    );
    return result;
};

export const getEarmoldOptions = async (
    catalogId: number,
    designId: number,
    isSnapFit2: boolean
) => {
    const result = await apiGet<IOption[]>(
        `${baseUrl}/get-earmold-options/catalogs/${catalogId}/design/${designId}/${isSnapFit2}`
    );
    return result;
};

export const getRelatedItems = async (partNumber: string) => {
    const result = await apiGet<{
        accessories: IRelatedItem[];
        commonSupplies: IRelatedItem[];
    }>(`${baseUrl}/get-related-items/${partNumber}`);
    return result;
};

export const getReturningProductDetails = async (
    guid: string,
    orderNumber: string
): Promise<IResult<IProductDetail>> => {
    const result = await apiGet<IProductDetail>(
        `${orderBaseUrl}/adjust-features/${orderNumber}/${guid}`
    );
    return result;
};
//#endregion
